/**
 * File: Footer.jsx
 * Path: /src/components/common/Footer.jsx
 * Description: Redesigned footer component with enhanced visual separation and modern styling.
 * Key Features: White background with accent border, multi-column layout, and subtle branding elements.
 * Maintenance Notes: Update when changing contact information or adding new footer sections.
 */

import React from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-white text-dark-800 relative overflow-hidden">
      {/* Top decorative border */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-400 via-accent-400 to-secondary-400"></div>
      
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="subtle-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#subtle-grid)" />
        </svg>
      </div>
      
      {/* Floating decorative elements */}
      <div className="absolute top-40 right-10 w-64 h-64 bg-primary-100 rounded-full blur-3xl opacity-20"></div>
      <div className="absolute bottom-20 left-10 w-48 h-48 bg-secondary-100 rounded-full blur-3xl opacity-20"></div>
      
      <div className="container-padding mx-auto py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Logo and description */}
          <div className="col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-5">
              <span className="font-display text-2xl font-bold">
                <span className="text-primary-600">Veritect</span>
                <span className="text-accent-500">.ai</span>
              </span>
            </Link>
            <p className="text-dark-600 max-w-md mb-6">
              Transforming legal research, contract analysis, and judgement prediction
              with cutting-edge AI solutions for modern law firms.
            </p>
            <div className="flex space-x-5 mt-6">
              {/* Social icons */}
              <a href="https://linkedin.com" className="text-dark-400 hover:text-primary-600 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M0 0v24h24v-24h-24zm8 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.397-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a href="https://youtube.com" className="text-dark-400 hover:text-primary-600 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
              </a>
            </div>
          </div>
          
          {/* Quick Links */}
<div>
  <h3 className="text-lg font-semibold mb-5 text-primary-600">Quick Links</h3>
  <ul className="space-y-3">
    <li>
      <Link to="/" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Home
      </Link>
    </li>
    <li>
      <Link to="/about" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        About Us
      </Link>
    </li>
    <li>
      <Link to="/blog" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Blog
      </Link>
    </li>
    <li>
      <Link to="/careers" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Careers
      </Link>
    </li>
    <li>
      <Link to="/contact" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Contact
      </Link>
    </li>
  </ul>
</div>
          
          {/* Solutions */}
<div>
  <h3 className="text-lg font-semibold mb-5 text-primary-600">Our Solutions</h3>
  <ul className="space-y-3">
    <li>
      <Link to="/solutions/legal-research" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Legal Research
      </Link>
    </li>
    <li>
      <Link to="/solutions/contract-analysis" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Contract Analysis
      </Link>
    </li>
    <li>
      <Link to="/solutions/legal-chronologies" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Legal Chronologies
      </Link>
    </li>
    <li>
      <Link to="/solutions/judgment-prediction" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Judgment Prediction
      </Link>
    </li>
    <li>
      <Link to="/solutions/ocr-translation" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        OCR & Translation
      </Link>
    </li>
    <li>
      <Link to="/solutions/case-management" className="text-dark-600 hover:text-primary-600 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Case Management
      </Link>
    </li>
  </ul>
</div>
          
          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold mb-5 text-primary-600">Contact Information</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="text-primary-600 mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-dark-600">
                    160 Robinson Road #14-04 <br/>
                    Singapore Business Federation Center <br/>
                    Singapore SGP 068914
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="text-primary-600 mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <a href="mailto:info@veritect.ai" className="text-dark-600 hover:text-primary-600 transition-colors">
                    info@veritect.ai
                  </a>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="text-primary-600 mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <a href="tel:+6512345678" className="text-dark-600 hover:text-primary-600 transition-colors">
                    +65 1234 5678
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-dark-100 mt-14 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-dark-500 text-sm mb-4 md:mb-0">
            &copy; {currentYear} Veritect.ai. All rights reserved.
          </p>
          <div className="flex space-x-6 text-sm text-dark-500">
            <Link to="/privacy" className="hover:text-primary-600 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-primary-600 transition-colors">
              Terms of Service
            </Link>
            <Link to="/cookies" className="hover:text-primary-600 transition-colors">
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;